import dayjs from '@/dayjs'
import {PatientSmall} from '@/features/patients/types'
import {useGetProjectsByPatientDate} from '@/features/projects/queries/useGetProjectsByPatientDate'
import {ProjectSmall} from '@/features/projects/types'
import {useToggle} from '@/hooks/useToggle'
import {useCallback, useEffect, useMemo, useRef} from 'react'

/**
 * Custom hook to fetch and manage the project associated with a patient and a given report date.
 * This hook:
 * - Retrieves the project linked to the specified `patientId` and `date` using `useGetProjectByPatientDate`.
 * - Automatically updates the current project state (`setCurrentProject`) when the fetched data changes.
 * - Handles error state visibility using `useToggle`, showing an error element if the query fails.
 */

interface UseProjectsByPatientDate {
    patientId: PatientSmall['id']
    date: Date
    currentProject?: ProjectSmall | null
    setCurrentProject?: (project: ProjectSmall | null) => void
    enabled?: boolean
}

export const useProjectsByPatientDate = ({
    patientId,
    date,
    currentProject,
    setCurrentProject,
    enabled = true
}: UseProjectsByPatientDate) => {
    // Manage the visibility of the error element using a toggle hook
    const {isActive: isErrorElement, on: showErrorElement, off: hideErrorElement} = useToggle()

    // Fetch the project associated with the given patient and report date
    const {
        data: projectsByPatient,
        status: projectsByPatientStatus,
        isError,
        isLoading
    } = useGetProjectsByPatientDate({
        patientId,
        date: dayjs(date).format('YYYY-MM-DD'),
        // disable cache and refetch on mount to avoid issues with the project selection
        options: {
            gcTime: 0,
            staleTime: 0,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            enabled
        }
    })

    // Memoize the project to avoid rerendering triggered by useEffect. The two empty values are kept
    // apart on purpose: `undefined` means the query has not resolved yet, while `null` is the
    // NoContent response, i.e. no project covers that date.
    const projects = useMemo(() => projectsByPatient, [projectsByPatient])

    // Tracks the project this hook pushed, so a `null` it set (no project covering that date) is not
    // mistaken for the user deliberately clearing the project through the project select.
    const autoSetProjectIdRef = useRef<string | null>()

    // Update the project state when the fetched project changes
    const handleSetCurrentProject = useCallback(() => {
        // The date is not covered by any project: the report must be detached from the stale one
        const nextProject = projects?.[0] ?? null

        // The user explicitly cleared the project: stop syncing it from the date
        if (currentProject === null && autoSetProjectIdRef.current !== null) return

        if ((nextProject?.id ?? null) === (currentProject?.id ?? null)) return

        autoSetProjectIdRef.current = nextProject?.id ?? null
        setCurrentProject?.(nextProject)
    }, [currentProject, projects, setCurrentProject])

    // Skip the very first sync if a project was already pre-selected (e.g. from a projectId query
    // param), so it isn't silently replaced by the date-based lookup on initial load. Subsequent
    // date changes still trigger the sync as before.
    const hasAutoSyncedRef = useRef(false)

    useEffect(() => {
        if (projects === undefined) return

        if (!hasAutoSyncedRef.current && currentProject) {
            hasAutoSyncedRef.current = true
            return
        }

        hasAutoSyncedRef.current = true
        handleSetCurrentProject()
    }, [projects])

    // Manage the visibility of the error element based on the query status
    useEffect(() => {
        if (projectsByPatientStatus == 'error') {
            showErrorElement()
        } else if (projectsByPatientStatus == 'success') {
            hideErrorElement()
        }
    }, [projectsByPatientStatus, showErrorElement, hideErrorElement])

    return {isErrorElement, hideErrorElement, projects, isError, isLoading}
}
